import axios from "axios"
import React, { useEffect, useState } from "react"
import Swal from "sweetalert2"
import { Dialog } from "primereact/dialog"
import { Calendar } from "primereact/calendar"
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { Editor } from "primereact/editor"
import { Col, Row } from "reactstrap"
import LinkContactsPopup from "ATSComponents/Common/LinkContactsPopup"
import CompanySelector from "ATSComponents/Common/CompanySelector"

const priorityValue = [
  { name: "Low", value: "low" },
  { name: "Medium", value: "medium" },
  { name: "High", value: "high" },
]

const formatDate = date => {
  if (!date) return ""
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatTime = time => {
  if (!time) return ""
  const t = new Date(time)
  const hours = String(t.getHours()).padStart(2, "0")
  const minutes = String(t.getMinutes()).padStart(2, "0")
  const seconds = String(t.getSeconds()).padStart(2, "0")
  return `${hours}:${minutes}:${seconds}`
}

const getCandidateFullName = candidate =>
  [candidate?.first_name, candidate?.middle_name, candidate?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || "Candidate"

const getInterviewJobLabel = job =>
  [job?.title, job?.job_number].filter(Boolean).join(", ").trim()

const extractCandidateJobs = candidate => {
  const linkedJobs = Array.isArray(candidate?.linked_jobs) ? candidate.linked_jobs : []

  if (linkedJobs.length > 0) {
    return linkedJobs
      .map(job => ({
        id: job?.job ?? job?.job_id ?? job?.id,
        title: job?.job_name || job?.job_title || job?.title || job?.job || "",
        job_number: job?.job_number || "",
        company_id: candidate?.company || job?.company || candidate?.company_id || null,
      }))
      .filter(job => job.id || job.title)
  }

  const directJobId =
    candidate?.job_id ??
    candidate?.job_ids ??
    candidate?.job?.job_id ??
    candidate?.job?.id ??
    candidate?.linked_job_id ??
    null

  if (directJobId) {
    const normalizedJobIds = String(directJobId).split(",").map(id => id.trim()).filter(Boolean)
    return normalizedJobIds
      .map((jobId, index) => ({
        id: jobId,
        title:
          candidate?.job_titles?.split(",")?.[index]?.trim() ||
          candidate?.job_titles?.trim() ||
          candidate?.job_title?.trim() ||
          "",
        job_number: candidate?.job_number || "",
        company_id: candidate?.company || candidate?.company_id || null,
      }))
      .filter(job => job.id || job.title)
  }

  if (candidate?.job_titles) {
    return String(candidate.job_titles)
      .split(",")
      .map(title => title.trim())
      .filter(Boolean)
      .map((title, index) => ({
        id: `${candidate?.candidate_id || "candidate"}-${index}`,
        title,
        job_number: "",
        company_id: candidate?.company || candidate?.company_id || null,
      }))
  }

  return []
}

const resolveCandidateId = candidate =>
  candidate?.candidate_id ?? candidate?.id ?? candidate?.value ?? null

const resolveCandidateJobId = (candidate, fallbackJobId) => {
  const linkedJobId = Array.isArray(candidate?.linked_jobs)
    ? candidate.linked_jobs.map(job => job?.job_id ?? job?.job ?? job?.id).find(Boolean)
    : null

  const directJobId =
    candidate?.job_id ??
    candidate?.job_ids ??
    candidate?.job?.job_id ??
    candidate?.job?.id ??
    candidate?.linked_job_id ??
    null

  if (directJobId) {
    return (
      String(directJobId).split(",").map(id => id.trim()).filter(Boolean)[0] ||
      fallbackJobId ||
      null
    )
  }

  return linkedJobId ?? fallbackJobId ?? null
}

// ─── Resolve candidate's company ID ──────────────────────────────────────────
const resolveCandidateCompanyId = (candidate, fallbackCompanyId) =>
  candidate?.company ||
  candidate?.company_id ||
  candidate?.company_data?.company_id ||
  candidate?.company_data?.id ||
  fallbackCompanyId ||
  null

const ScheduleInterviewDialog = ({
  visible,
  onHide,
  companyId,          // job-level fallback company id
  candidate,
  currentJobId,
  jobTitle,
  onScheduled,
}) => {
  const [subtypeCall, setSubTypeCall] = useState([])
  const [scheduleTypeId, setScheduleTypeId] = useState(null)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [scheduleSubtype, setScheduleSubtype] = useState(null)
  const [scheduleStartDate, setScheduleStartdate] = useState(new Date())
  const [scheduleStartTime, setScheduleStarttime] = useState(new Date())
  const [scheduleEndDate, setScheduleEndDate] = useState(new Date())
  const [scheduleEndTime, setScheduleEndTime] = useState(new Date())
  const [scheduleSubject, setScheduleSubject] = useState("")
  const [schedulePriority, setSchedulePriority] = useState(null)
  const [popTextares, setPopTextares] = useState("")
  const [selectedScheduleJobs, setSelectedScheduleJobs] = useState([])
  const [selectedScheduleContacts, setSelectedScheduleContacts] = useState([])
  const [selectedScheduleCandidates, setSelectedScheduleCandidates] = useState([])
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [selectedCcUsers, setSelectedCcUsers] = useState([])
  const [scheduleReminder] = useState(null)
  const [scheduleFormErrors, setScheduleFormErrors] = useState({})
  const [loadingSave, setLoadingSave] = useState(false)

  // ─── Company state ────────────────────────────────────────────────────────
  // selectedCompanyId is the raw numeric/string id we pass around
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)

  // ─── Derive effective company id whenever candidate or prop changes ───────
  useEffect(() => {
    const resolved = resolveCandidateCompanyId(candidate, companyId)
    setSelectedCompanyId(resolved ? String(resolved) : null)
  }, [candidate, companyId])

  const fetchInterviewTypes = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-schedule-type/?interview=true")
      if (response.status === 200) {
        const transformedData = response.data.results.map(type => ({
          name: type.schedule_type,
          code: type.id,
        }))
        const interviewType = transformedData.find(t => t.name === "Interview")
        if (interviewType) {
          setScheduleTypeId(interviewType.code)
          setSelectedSchedule(interviewType)
        }
      }
    } catch (error) {
      console.error("Error fetching interview types:", error)
    }
  }

  const fetchSubTypes = async id => {
    if (!id) return
    try {
      const response = await axios.get(`/api/v1/get-all-schedule-sub-type/?schedule_id=${id}`)
      const scheduleData = response.data?.results || []
      const transformedSubTypes = scheduleData.map(schedule => ({
        name: schedule.schedule_subtype || "Unnamed Sub-Type",
        code: schedule.id || "",
      }))
      setSubTypeCall(transformedSubTypes)
    } catch (error) {
      console.error("Error fetching sub-types:", error)
    }
  }

  useEffect(() => {
    if (scheduleTypeId) fetchSubTypes(scheduleTypeId)
  }, [scheduleTypeId])

  const resetForm = () => {
    setScheduleSubtype(null)
    setScheduleStartdate(new Date())
    setScheduleStarttime(new Date())
    setScheduleEndDate(new Date())
    setScheduleEndTime(new Date())
    setScheduleSubject("")
    setSchedulePriority(null)
    setPopTextares("")
    setSelectedScheduleJobs([])
    setSelectedScheduleContacts([])
    setSelectedScheduleCandidates([])
    setSelectedCustomers([])
    setSelectedCcUsers([])
    setScheduleFormErrors({})
  }

  useEffect(() => {
    if (!visible) return

    fetchInterviewTypes()
    resetForm()

    // Re-resolve company from candidate on each open
    const resolved = resolveCandidateCompanyId(candidate, companyId)
    setSelectedCompanyId(resolved ? String(resolved) : null)

    if (candidate) {
      setSelectedCustomers([candidate])
      setSelectedScheduleCandidates([candidate])
      const candidateJobs = extractCandidateJobs(candidate)
      setSelectedScheduleJobs(
        candidateJobs.length
          ? candidateJobs
          : currentJobId
          ? [{ id: currentJobId, title: jobTitle || "" }]
          : []
      )
      setScheduleSubject(
        `Interview - ${getCandidateFullName(candidate)}${jobTitle ? ` - ${jobTitle}` : ""}`
      )
    }
  }, [visible, candidate, currentJobId, jobTitle])

  const validateScheduleForm = () => {
    const errors = {}
    const now = new Date()

    if (!scheduleSubtype) errors.event_sub_type = "Sub-Type is required"
    if (!selectedCompanyId) errors.company = "Company is required"
    if (!selectedScheduleContacts || selectedScheduleContacts.length === 0) {
      errors.interviewer = "Interviewer is required"
    }
    if (!selectedScheduleJobs || selectedScheduleJobs.length === 0) {
      errors.job_id = "Job is required"
    }
    if (!selectedScheduleCandidates.length) errors.candidates = "Candidate is required"
    if (!scheduleSubject || scheduleSubject.trim() === "") {
      errors.subject = "Subject is required"
    }
    if (!schedulePriority) errors.priority = "Priority is required"

    if (scheduleStartDate && scheduleStartTime && scheduleEndDate && scheduleEndTime) {
      const startDateTime = new Date(scheduleStartDate)
      startDateTime.setHours(scheduleStartTime.getHours(), scheduleStartTime.getMinutes(), 0)

      const resolvedEndDate =
        scheduleEndDate && scheduleEndDate >= scheduleStartDate ? scheduleEndDate : scheduleStartDate
      const endDateTime = new Date(resolvedEndDate)
      endDateTime.setHours(scheduleEndTime.getHours(), scheduleEndTime.getMinutes(), 0)

      if (startDateTime < now) errors.start_time = "Start Time cannot be in the Past Time"
      if (endDateTime <= startDateTime) errors.end_time = "End Time must be after Start Time"
    }

    setScheduleFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveSchedule = async () => {
    if (!validateScheduleForm()) return
    setLoadingSave(true)

    const effectiveCompanyId = selectedCompanyId || companyId
    const apiUrl = `api/v1/events/?company_id=${effectiveCompanyId}`

    const payload = {
      event_sub_type: scheduleSubtype?.name || "Screening Call",
      event_type: "Interview",
      end_date: formatDate(
        scheduleEndDate && scheduleEndDate >= scheduleStartDate ? scheduleEndDate : scheduleStartDate
      ),
      start_date: formatDate(scheduleStartDate),
      start_time: formatTime(scheduleStartTime),
      end_time: formatTime(scheduleEndTime),
      candidate_id: selectedScheduleCandidates.map(resolveCandidateId).filter(Boolean),
      candidates: selectedScheduleCandidates.map(c => c.email).filter(Boolean),
      cc_users: selectedCcUsers,
      subject: scheduleSubject,
      job_id: [
        ...new Set(
          selectedScheduleCandidates
            .map(c => resolveCandidateJobId(c, currentJobId))
            .concat(selectedScheduleJobs.map(job => job?.id ?? job?.job_id ?? job?.job))
            .filter(Boolean)
        ),
      ],
      event_time: new Date(
        `${formatDate(scheduleStartDate)}T${formatTime(scheduleStartTime)}`
      ).toISOString(),
      event_priority: schedulePriority || "Low",
      private: false,
      completed: false,
      body: popTextares,
      contact_id: selectedScheduleContacts,
      reminder: scheduleReminder?.value || 10,
    }

    try {
      await axios.post(apiUrl, payload, {})
      Swal.fire({
        title: "Success!",
        text: "Interview scheduled successfully!",
        icon: "success",
        confirmButtonText: "OK",
      })
      resetForm()
      onHide()
      if (onScheduled) onScheduled()
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error || "An unknown error occurred. Please try again later."
      Swal.fire({
        title: "Error!",
        text: `${errorMessage}`,
        icon: "error",
        confirmButtonText: "OK",
      })
    } finally {
      setLoadingSave(false)
    }
  }

  return (
    <Dialog
      header="Schedule Interview"
      visible={visible}
      className="interview-popup"
      style={{ width: "50vw" }}
      onHide={() => { onHide() }}
    >
      <form>
        <div className="mb-4">
          {/* Type + Sub-Type */}
          <Row className="mb-2">
            <Col xl={6}>
              <div className="p-field flex flex-column">
                <label htmlFor="interview">
                  Type <span className="text-danger">*</span>
                </label>
                <InputText disabled value={"Interview"} />
              </div>
            </Col>
            <Col xl={6}>
              <div className="p-field flex flex-column" id="SubTypeDropdown">
                <label htmlFor="integer" className="block">
                  Sub-Type <span className="text-danger">*</span>
                </label>
                <Dropdown
                  value={scheduleSubtype}
                  onChange={e => {
                    setScheduleSubtype(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, event_sub_type: "" }))
                  }}
                  options={subtypeCall}
                  optionLabel="name"
                  placeholder="Subtype"
                  className="w-full search-option"
                />
                {scheduleFormErrors.event_sub_type && (
                  <small className="p-error">{scheduleFormErrors.event_sub_type}</small>
                )}
              </div>
            </Col>
          </Row>

          {/* ─── Company — bound to candidate's company, changeable by user ─── */}
          <Row className="mb-2">
            <Col xl={12}>
              <div className="p-field flex flex-column">
                <label htmlFor="company" className="block">
                  Company <span className="text-danger">*</span>
                </label>
                <CompanySelector
                  selectedCompanyId={selectedCompanyId}
                  onCompanyChange={company => {
                    const id =
                      company?.company_id ??
                      company?.id ??
                      company?.code ??
                      null
                    setSelectedCompanyId(id ? String(id) : null)
                    setScheduleFormErrors(prev => ({ ...prev, company: "" }))
                  }}
                />
                {scheduleFormErrors.company && (
                  <small className="p-error">{scheduleFormErrors.company}</small>
                )}
              </div>
            </Col>
          </Row>

          {/* Date + Time */}
          <Row className="mb-2">
            <Col xl={5}>
              <div className="p-field flex-auto">
                <label htmlFor="startDate" className="block">
                  Start Date <span className="text-danger">*</span>
                </label>
                <Calendar
                  id="startDate"
                  className="w-100"
                  value={scheduleStartDate}
                  onChange={e => {
                    setScheduleStartdate(e.value)
                    setScheduleEndDate(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, start_time: "", end_time: "" }))
                  }}
                  showIcon
                  minDate={new Date()}
                  dateFormat="yy-mm-dd"
                  icon={() => <i className="pi pi-calendar" />}
                />
              </div>
            </Col>
            <Col xl={4}>
              <div className="p-field flex-auto">
                <label htmlFor="startTime" className="block">
                  Start Time <span className="text-danger">*</span>
                </label>
                <Calendar
                  id="startTime"
                  className="w-100"
                  value={scheduleStartTime}
                  onChange={e => {
                    setScheduleStarttime(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, start_time: "", end_time: "" }))
                  }}
                  showIcon
                  timeOnly
                  hourFormat="12"
                  icon={() => <i className="pi pi-clock" />}
                />
                {scheduleFormErrors.start_time && (
                  <small className="p-error">{scheduleFormErrors.start_time}</small>
                )}
              </div>
            </Col>
            <Col xl={3}>
              <div className="flex-auto">
                <label htmlFor="endTime" className="block">
                  End Time <span className="text-danger">*</span>
                </label>
                <Calendar
                  id="endTime"
                  value={scheduleEndTime}
                  onChange={e => {
                    setScheduleEndTime(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, end_time: "" }))
                  }}
                  showIcon
                  timeOnly
                  hourFormat="12"
                  icon={() => <i className="pi pi-clock" />}
                />
                {scheduleFormErrors.end_time && (
                  <small className="p-error">{scheduleFormErrors.end_time}</small>
                )}
              </div>
            </Col>
          </Row>
        </div>

        <div className="mb-4">
          <Row className="mb-2">
            <LinkContactsPopup
              hideJobSelector
              onSelectedJobsChange={jobs => {
                setSelectedScheduleJobs(jobs)
                setScheduleFormErrors(prev => ({ ...prev, job_id: "" }))
              }}
              errorJob={scheduleFormErrors.job_id}
              setSelectedScheduleContacts={contacts => {
                setSelectedScheduleContacts(contacts)
                setScheduleFormErrors(prev => ({ ...prev, interviewer: "" }))
              }}
              setSelectedCcUsers={setSelectedCcUsers}
              clearInterviewerError={() =>
                setScheduleFormErrors(prev => ({ ...prev, interviewer: "" }))
              }
              errorContact={scheduleFormErrors.interviewer}
              // pass the resolved company id so LinkContactsPopup fetches the right interviewers
              companyId={selectedCompanyId || companyId}
              jobLabel={
                <span>
                  Jobs <span className="text-danger">*</span>
                </span>
              }
            />
          </Row>

          <Row className="mb-2">
            <Col xl={6}>
              <div className="field">
                <label htmlFor="selectedCandidate" style={{ marginBottom: "4px" }}>
                  Candidate <span className="text-danger">*</span>
                </label>
                <InputText
                  id="selectedCandidate"
                  disabled
                  className="w-full"
                  value={
                    selectedCustomers?.length > 0
                      ? selectedCustomers.map(c => getCandidateFullName(c)).join(" | ")
                      : ""
                  }
                  placeholder="Candidate"
                />
                {scheduleFormErrors.candidates && (
                  <small className="p-error">{scheduleFormErrors.candidates}</small>
                )}
              </div>
            </Col>
            <Col xl={6}>
              <div className="field">
                <label htmlFor="selectedJob" style={{ marginBottom: "4px" }}>
                  Job <span className="text-danger">*</span>
                </label>
                <InputText
                  id="selectedJob"
                  disabled
                  className="w-full"
                  value={(() => {
                    const jobLabels = (selectedScheduleJobs || [])
                      .map(getInterviewJobLabel)
                      .filter(Boolean)
                      .join(" | ")
                    return jobLabels || jobTitle || ""
                  })()}
                  placeholder="Job titles will appear here"
                />
                {scheduleFormErrors.job_id && (
                  <small className="p-error">{scheduleFormErrors.job_id}</small>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col xl={12}>
              <div className="p-field flex flex-column">
                <label htmlFor="subject">
                  Subject <span className="text-danger">*</span>
                </label>
                <InputText
                  placeholder="Subject"
                  value={scheduleSubject}
                  onChange={e => {
                    setScheduleSubject(e.target.value)
                    if (scheduleFormErrors.subject) {
                      setScheduleFormErrors(prev => ({ ...prev, subject: "" }))
                    }
                  }}
                />
                {scheduleFormErrors.subject && (
                  <small className="p-error">{scheduleFormErrors.subject}</small>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col xl={12}>
              <div>
                <label htmlFor="note">Note :</label>
                <Editor
                  value={popTextares}
                  onTextChange={e => setPopTextares(e.htmlValue || "")}
                  placeholder="Interview Notes"
                  style={{ height: "140px" }}
                />
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row className="mb-2">
            <Col xl={6}>
              <div className="p-field flex flex-column" id="PriorityDropdown">
                <label htmlFor="Priority" className="block">
                  Priority <span className="text-danger">*</span>
                </label>
                <Dropdown
                  value={schedulePriority}
                  onChange={e => {
                    setSchedulePriority(e.value)
                    if (scheduleFormErrors.priority) {
                      setScheduleFormErrors(prev => ({ ...prev, priority: "" }))
                    }
                  }}
                  options={priorityValue}
                  optionLabel="name"
                  placeholder="Priority"
                  className="w-full search-option"
                />
                {scheduleFormErrors.priority && (
                  <small className="p-error">{scheduleFormErrors.priority}</small>
                )}
              </div>
            </Col>
          </Row>
        </div>

        <Row className="">
          <Col xl={12}>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light me-2"
                onClick={() => { onHide() }}
              >
                <i className="pi pi-times me-1"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light btn btn-primary me-2 btn-main"
                onClick={handleSaveSchedule}
                disabled={loadingSave}
              >
                <i className="pi pi-save me-1"></i>
                {loadingSave ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </Col>
        </Row>
      </form>
    </Dialog>
  )
}

export default ScheduleInterviewDialog